"use client"

import React, { useCallback, useEffect, useRef, useState } from "react"
import { useChatContext } from "stream-chat-react"
import debounce from "lodash/debounce"
import { ChevronLeftIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline"
import { Avatar, Text } from "@/elements"
import { useLazyQuery } from "@apollo/client"
import { graphql } from "@/gql"
import { useMessages } from "./hooks/useMessages"
import { useGetOrCreateChannel } from "./hooks/useGetOrCreateChannel"
import { useSession } from "@/hooks/useSession"

const CreateChannelSearchUsers = graphql(`
  query CreateChannelSearchUsers($search: String, $isConnected: Boolean) {
    allIndividualCreatives(isConnectedWith: $isConnected, excludeMe: true, name: $search) {
      edges {
        node {
          id
          name
          displayName
          user {
            id
            uuid
            avatarInitials
            profile {
              id
              avatarUrl(width: 144)
            }
          }
        }
      }
    }
  }
`)

const UserResult: React.FC<{ edge: any }> = ({ edge }) => {
  const node = edge.node
  const name = node.displayName || node.name
  const user = node.user?.profile

  return (
    <li className="relative flex cursor-pointer flex-row items-center rounded-md p-2 hover:bg-bg-2">
      <Avatar src={user?.avatarUrl} initials={node?.user?.avatarInitials} size="xs" />
      <div className="ml-2">
        <Text element="span">{name}</Text>
      </div>
    </li>
  )
}

export const CreateChannel: React.FC = () => {
  const session = useSession()
  const { setActiveChannel, client } = useChatContext()
  const [focusedUser, setFocusedUser] = useState<number | undefined>(undefined)
  const [inputText, setInputText] = useState("")
  const [resultsOpen, setResultsOpen] = useState(false)
  const [noResults, setNoResults] = useState(false)
  const [searching, setSearching] = useState(false)
  const [users, setUsers] = useState<any>([])
  const sessionUserIsClient = session.data?.account === "client"

  const { getOrCreateChannel } = useGetOrCreateChannel(client)
  const { setIsCreating, toggleMobileChannel } = useMessages()
  const [searchPeopleQuery] = useLazyQuery(CreateChannelSearchUsers, {
    variables: { search: inputText, isConnected: session.data?.account === "creative" },
  })
  const inputRef = useRef<HTMLInputElement>(null)

  const clearState = useCallback(() => {
    setInputText("")
    setResultsOpen(false)
  }, [])

  useEffect(() => {
    const clickListener = () => {
      if (resultsOpen) clearState()
    }

    document.addEventListener("click", clickListener)
    return () => document.removeEventListener("click", clickListener)
  }, [resultsOpen, clearState])

  const findUsers = useCallback(
    debounce(async () => {
      if (searching) return
      setSearching(true)

      try {
        const { data } = await searchPeopleQuery()
        const edges = data?.allIndividualCreatives?.edges || []

        setUsers(edges)
        setNoResults(!edges.length)
        setResultsOpen(true)
      } catch (error) {
        console.error(error)
      }

      setSearching(false)
    }, 100),
    [searchPeopleQuery, searching]
  )

  useEffect(() => {
    if (inputText) {
      findUsers()
    }
  }, [inputText, findUsers])

  const startChat = useCallback(
    async (edge: any) => {
      const channel = await getOrCreateChannel({
        creativeId: edge.node.id,
        userUuid: edge.node.user.uuid,
        channelType: sessionUserIsClient ? "client_to_creative_networking" : "creative_networking",
        sessionUserIsClient,
      })
      await channel.watch()
      setActiveChannel(channel)
      setIsCreating(false)
    },
    [getOrCreateChannel, sessionUserIsClient, setActiveChannel, setIsCreating]
  )

  const handleKeyDown = useCallback(
    (e: any) => {
      if (e.which === 38) {
        setFocusedUser(prevFocused =>
          prevFocused === undefined ? 0 : prevFocused === 0 ? users.length - 1 : prevFocused - 1
        )
      } else if (e.which === 40) {
        setFocusedUser(prevFocused =>
          prevFocused === undefined ? 0 : prevFocused === users.length - 1 ? 0 : prevFocused + 1
        )
      } else if (e.which === 13 && focusedUser !== undefined) {
        e.preventDefault()
        startChat(users[focusedUser])
        setFocusedUser(undefined)
      }
    },
    [users, focusedUser, startChat]
  )

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [handleKeyDown])

  const onMobileBack = () => {
    setIsCreating(false)
    toggleMobileChannel()
  }

  return (
    <div className="w-full">
      <header className="flex w-full flex-col bg-bg-2 px-4 py-3 md:bg-bg-1">
        <div className="flex flex-row items-center pb-4 pt-3 md:hidden">
          <div onClick={onMobileBack} className="pr-4">
            <ChevronLeftIcon height={24} width={24} />
          </div>
          <Text variant="h5">New message</Text>
        </div>
        <div className="flex w-full flex-row items-center">
          <div className="w-full">
            <div className="flex w-full flex-row items-center rounded-full bg-bg-2 px-3 py-2">
              <MagnifyingGlassIcon height={22} className="flex-shink-0 pr-2" />
              <input
                autoFocus
                ref={inputRef}
                value={inputText}
                onChange={e => setInputText(e.target.value)}
                placeholder="Search"
                type="search"
                className="w-full border-none bg-bg-2 p-0 focus:outline-none focus:ring-0"
              />
            </div>
          </div>
        </div>
      </header>
      {inputText && (
        <main className="max-w-[400px] px-4">
          <div className="rounded-xl border border-bg-2 bg-bg-1 p-3">
            {!!users?.length ? (
              <div className="flex flex-col gap-3">
                {users.map((edge: any, index: number) => (
                  <div
                    className={`${focusedUser === index && "focused"}`}
                    onClick={() => startChat(edge)}
                    key={edge?.node?.id}>
                    <UserResult edge={edge} />
                  </div>
                ))}
              </div>
            ) : (
              <div
                onClick={() => {
                  inputRef?.current?.focus()
                  clearState()
                }}
                className="empty">
                <Text>{noResults ? "No contacts found" : "Loading..."}</Text>
              </div>
            )}
          </div>
        </main>
      )}
    </div>
  )
}
